// ═══════════════════════════════════════════════════════════════
// TasksView — Director Task Manager
// Path: src/views/TasksView.jsx
//
// Zone-compliant flat task list grouped by due proximity.
// Directors see own tasks; admin sees all with director selector.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  ClipboardCheck,
  Plus,
  ChevronDown,
  ChevronRight,
  Archive,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { ActionButton } from "../components/ui/ActionButton";
import { FormSelect } from "../components/ui/FormControls";
import { TaskRow } from "../components/tasks/TaskRow";
import { QuickTaskModal } from "../components/features/QuickTaskModal";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { TASK_PRIORITIES, TASK_CATEGORIES } from "../constants/taskTypes";
import { HELP } from "../constants/helpContent";

// ── Grouping helper ──────────────────────────────────────────

const groupTasks = (tasks) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split("T")[0];

  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + 7);
  const weekStr = endOfWeek.toISOString().split("T")[0];

  const groups = {
    overdue: [],
    today: [],
    thisWeek: [],
    later: [],
    noDate: [],
  };

  tasks.forEach((task) => {
    if (!task.dueDate) {
      groups.noDate.push(task);
    } else if (task.dueDate < todayStr) {
      groups.overdue.push(task);
    } else if (task.dueDate === todayStr) {
      groups.today.push(task);
    } else if (task.dueDate <= weekStr) {
      groups.thisWeek.push(task);
    } else {
      groups.later.push(task);
    }
  });

  // Sort overdue oldest first, rest by due date ascending
  groups.overdue.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  groups.today.sort((a, b) => (getPrioritySortOrder(a) - getPrioritySortOrder(b)));
  groups.thisWeek.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  groups.later.sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  groups.noDate.sort((a, b) => (getPrioritySortOrder(a) - getPrioritySortOrder(b)));

  return groups;
};

const getPrioritySortOrder = (task) => {
  const p = TASK_PRIORITIES.find((pr) => pr.id === task.priority);
  return p ? p.sortOrder : 2;
};

// ── Section header ───────────────────────────────────────────

const CollapsibleSection = ({ label, count, color, palette, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: FONT_FAMILY,
          width: "100%",
        }}
      >
        {open ? (
          <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
        ) : (
          <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
        )}
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: color || palette.text2,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: palette.text3,
          }}
        >
          ({count})
        </span>
      </button>
      {open && children}
    </div>
  );
};

// ── TasksView ────────────────────────────────────────────────

export const TasksView = () => {
  const {
    palette,
    members,
    directors,
    directorTasks,
    openTasks,
    taskBadgeCount,
    addTask,
    completeTask,
    updateTask,
    deleteTask,
    loadTasksWithArchive,
    loadTasksStandard,
    navigate,
    goBack,
    goHome,
    isAdmin,
    userProfile,
    can,
  } = useApp();

  const [filterPriority, setFilterPriority] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterDirector, setFilterDirector] = useState("all");
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [archiveOn, setArchiveOn] = useState(false);

  // Swap the tasks data source when the archive toggle flips. On
  // unmount we restore the capped load so the drawer and My Day
  // sidebar don't inherit a bloated list.
  useEffect(() => {
    if (archiveOn) {
      loadTasksWithArchive();
    } else {
      loadTasksStandard();
    }
    return () => {
      // If the view is unmounting while archive was on, reset to
      // standard so other surfaces stay fast.
      if (archiveOn) loadTasksStandard();
    };
  }, [archiveOn]);

  // Current user's director name (for non-admin default)
  const currentDirectorName = userProfile?.full_name || "";

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return directorTasks.filter((task) => {
      if (filterStatus === "open" && task.status !== "open") return false;
      if (filterStatus === "completed" && task.status !== "completed") return false;
      if (filterPriority !== "all" && task.priority !== filterPriority) return false;
      if (filterCategory !== "all" && task.category !== filterCategory) return false;
      if (filterDirector !== "all" && task.directorName !== filterDirector) return false;
      return true;
    });
  }, [directorTasks, filterStatus, filterPriority, filterCategory, filterDirector]);

  // Group open tasks
  const openFiltered = filteredTasks.filter((t) => t.status === "open");
  const completedFiltered = filteredTasks.filter((t) => t.status === "completed");
  const groups = useMemo(() => groupTasks(openFiltered), [openFiltered]);

  const openCount = openTasks.length;
  const subtitle = `${openCount} open${taskBadgeCount > 0 ? ` · ${taskBadgeCount} due` : ""}`;

  const handleNavigateToDonor = (member) => {
    if (member) navigate("memberDetail", member);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleDelete = async (taskId) => {
    await deleteTask(taskId);
  };

  const handleComplete = async (taskId) => {
    await completeTask(taskId);
  };

  const handleReopen = async (taskId) => {
    await updateTask(taskId, { status: "open", completedAt: null });
  };

  const handleSaveTask = async (taskData) => {
    if (editingTask) {
      await updateTask(editingTask.id, taskData);
    } else {
      await addTask(taskData);
    }
    setShowTaskModal(false);
    setEditingTask(null);
  };

  // Section rendering helper
  const renderSection = (label, tasks, color, defaultOpen) => {
    if (tasks.length === 0) return null;
    return (
      <CollapsibleSection
        key={label}
        label={label}
        count={tasks.length}
        color={color}
        palette={palette}
        defaultOpen={defaultOpen}
      >
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            palette={palette}
            members={members}
            onComplete={handleComplete}
            onReopen={handleReopen}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onNavigateToDonor={handleNavigateToDonor}
          />
        ))}
      </CollapsibleSection>
    );
  };

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="My Tasks"
        subtitle={subtitle}
        icon={ClipboardCheck}
        onBack={goBack}
        onHome={goHome}
        helpContent={HELP.tasks}
      />
      <ZoneTwoToolbar />

      {/* Zone 2: Local filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 24px",
          borderBottom: `1px solid ${palette.cardBorder}`,
          flexWrap: "wrap",
          fontFamily: FONT_FAMILY,
        }}
      >
        {isAdmin && (
          <FormSelect
            value={filterDirector}
            onChange={setFilterDirector}
            options={[
              { value: "all", label: "All Directors" },
              ...(directors || []).map((d) => ({
                value: d.name,
                label: d.name,
              })),
            ]}
          />
        )}
        <FormSelect
          value={filterPriority}
          onChange={setFilterPriority}
          options={[
            { value: "all", label: "All Priorities" },
            ...TASK_PRIORITIES.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
        <FormSelect
          value={filterCategory}
          onChange={setFilterCategory}
          options={[
            { value: "all", label: "All Categories" },
            ...TASK_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <FormSelect
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: "open", label: "Open" },
            { value: "completed", label: "Completed" },
            { value: "all", label: "All" },
          ]}
        />
        <div style={{ flex: 1 }} />
        <ActionButton
          label={archiveOn ? "Archive: On" : "Show archive"}
          icon={Archive}
          onClick={() => setArchiveOn(!archiveOn)}
          variant={archiveOn ? "primary" : "secondary"}
          small
          title={
            archiveOn
              ? "Showing all completed tasks. Toggle off to return to the last 30 days."
              : "Load completed tasks older than 30 days"
          }
        />
        <ActionButton
          label="New Task"
          icon={Plus}
          onClick={() => {
            setEditingTask(null);
            setShowTaskModal(true);
          }}
          small
        />
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 16px 16px",
          fontFamily: FONT_FAMILY,
        }}
      >
        {openFiltered.length === 0 && filterStatus !== "completed" && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 24px",
              color: palette.text3,
            }}
          >
            <ClipboardCheck
              size={ICON_SIZES.xxl}
              style={{ color: palette.text3, opacity: 0.3, marginBottom: 12 }}
            />
            <p style={{ fontSize: FONT_SIZES.sm, fontWeight: 500 }}>
              No tasks yet
            </p>
            <p style={{ fontSize: FONT_SIZES.xs, marginTop: 4 }}>
              Tasks are auto-created when you advance portfolio moves, or you can
              add your own.
            </p>
          </div>
        )}

        {filterStatus !== "completed" && (
          <>
            {renderSection("Overdue", groups.overdue, "#ef4444", true)}
            {renderSection("Today", groups.today, "#f59e0b", true)}
            {renderSection("This Week", groups.thisWeek, "#3b82f6", true)}
            {renderSection("Later", groups.later, null, true)}
            {renderSection("No Date", groups.noDate, null, true)}
          </>
        )}

        {/* Completed tasks */}
        {(filterStatus === "completed" || filterStatus === "all") &&
          completedFiltered.length > 0 && (
            <div style={{ marginTop: filterStatus === "completed" ? 0 : 16 }}>
              {filterStatus !== "completed" && (
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 16px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: FONT_FAMILY,
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontWeight: 600,
                  }}
                >
                  {showCompleted ? (
                    <ChevronDown size={ICON_SIZES.sm} />
                  ) : (
                    <ChevronRight size={ICON_SIZES.sm} />
                  )}
                  Show completed ({completedFiltered.length})
                </button>
              )}
              {(showCompleted || filterStatus === "completed") &&
                completedFiltered.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    palette={palette}
                    members={members}
                    onComplete={handleComplete}
                    onReopen={handleReopen}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onNavigateToDonor={handleNavigateToDonor}
                  />
                ))}
            </div>
          )}
      </div>

      <ZoneThreeFooter />

      {/* Quick Task Modal */}
      {showTaskModal && (
        <QuickTaskModal
          onClose={() => {
            setShowTaskModal(false);
            setEditingTask(null);
          }}
          onSave={handleSaveTask}
          editingTask={editingTask}
          directors={directors}
          members={members}
          defaultDirector={isAdmin ? "" : currentDirectorName}
        />
      )}
    </ViewLayout>
  );
};
